import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import PublicLayout from "@/components/PublicLayout";

// Public site
import PublicHome from "@/pages/public/PublicHome";
import PublicShop from "@/pages/public/PublicShop";
import PublicNews from "@/pages/public/PublicNews";
import PublicCareers from "@/pages/public/PublicCareers";
import PublicFeedback from "@/pages/public/PublicFeedback";
import TenantSignup from "@/pages/TenantSignup";

// Auth + admin
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Issues from "@/pages/Issues";
import Inventory from "@/pages/Inventory";
import Assets from "@/pages/Assets";
import Vehicles from "@/pages/Vehicles";
import FuelLogs from "@/pages/FuelLogs";
import Accidents from "@/pages/Accidents";
import Maintenance from "@/pages/Maintenance";
import Suppliers from "@/pages/Suppliers";
import Contracts from "@/pages/Contracts";
import Deliveries from "@/pages/Deliveries";
import Purchases from "@/pages/Purchases";
import Feedback from "@/pages/Feedback";
import ViewFeedback from "@/pages/ViewFeedback";
import BranchFeedback from "@/pages/BranchFeedback";
import Surveys from "@/pages/Surveys";
import LoyaltyMembers from "@/pages/LoyaltyMembers";
import BusinessCards from "@/pages/BusinessCards";
import Messages from "@/pages/Messages";
import Announcements from "@/pages/Announcements";
import Documents from "@/pages/Documents";
import Branches from "@/pages/Branches";
import Users from "@/pages/Users";
import Staff from "@/pages/Staff";
import Approvals from "@/pages/Approvals";
import AuditLog from "@/pages/AuditLog";
import Reports from "@/pages/Reports";
import Policy from "@/pages/Policy";
import TenantsAdmin from "@/pages/TenantsAdmin";
import Marketing from "@/pages/Marketing";
import PublicSignup from "@/pages/public/PublicSignup";
import PublicLogin from "@/pages/public/PublicLogin";
import PublicAccount from "@/pages/public/PublicAccount";
import PublicVerifyOTP from "@/pages/public/PublicVerifyOTP";
import PublicForgotPassword from "@/pages/public/PublicForgotPassword";
import PublicResetPassword from "@/pages/public/PublicResetPassword";
import PublicAuthCallback from "@/pages/public/PublicAuthCallback";
import AuthCallback from "@/pages/AuthCallback";
import InstallPrompt from "@/components/InstallPrompt";

// Website admin
import Products from "@/pages/Products";
import Orders from "@/pages/Orders";
import NewsAdmin from "@/pages/NewsAdmin";
import CareersAdmin from "@/pages/CareersAdmin";
import StockRequests from "@/pages/StockRequests";
import HeroSlides from "@/pages/HeroSlides";
import AdminRequests from "@/pages/AdminRequests";
import ApprovalPolicies from "@/pages/ApprovalPolicies";
import PeriodicReports from "@/pages/PeriodicReports";
import ExecutiveRollup from "@/pages/ExecutiveRollup";
import Backups from "@/pages/Backups";
import Luggage from "@/pages/Luggage";
import Attendance from "@/pages/Attendance";
import Leave from "@/pages/Leave";
import ModuleGuard from "@/components/ModuleGuard";

const G = (key, el) => <ModuleGuard moduleKey={key}>{el}</ModuleGuard>;

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <InstallPrompt />
        <Routes>
          {/* Public website (no auth) */}
          <Route path="/site" element={<PublicLayout />}>
            <Route index element={<PublicHome />} />
            <Route path="shop" element={<PublicShop />} />
            <Route path="news" element={<PublicNews />} />
            <Route path="careers" element={<PublicCareers />} />
            <Route path="careers/:id" element={<PublicCareers />} />
            <Route path="contact" element={<PublicFeedback />} />
            <Route path="register" element={<PublicSignup />} />
            <Route path="login" element={<PublicLogin />} />
            <Route path="verify" element={<PublicVerifyOTP />} />
            <Route path="forgot-password" element={<PublicForgotPassword />} />
            <Route path="reset-password" element={<PublicResetPassword />} />
            <Route path="auth-callback" element={<PublicAuthCallback />} />
            <Route path="account" element={<PublicAccount />} />
          </Route>
          <Route path="/signup" element={<TenantSignup />} />
          <Route path="/login" element={<Login />} />
          <Route path="/auth-callback" element={<AuthCallback />} />
          <Route path="/marketing" element={<Marketing />} />

          {/* Admin (auth required) */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="executive-rollup" element={G("executive-rollup", <ExecutiveRollup />)} />
            <Route path="reports" element={G("reports", <Reports />)} />
            <Route path="issues" element={G("issues", <Issues />)} />
            <Route path="inventory" element={G("inventory", <Inventory />)} />
            <Route path="stock-requests" element={G("stock-requests", <StockRequests />)} />
            <Route path="assets" element={G("assets", <Assets />)} />
            <Route path="vehicles" element={G("vehicles", <Vehicles />)} />
            <Route path="fuel-logs" element={G("fuel-logs", <FuelLogs />)} />
            <Route path="accidents" element={G("accidents", <Accidents />)} />
            <Route path="maintenance" element={G("maintenance", <Maintenance />)} />
            <Route path="luggage" element={G("luggage", <Luggage />)} />
            <Route path="attendance" element={G("attendance", <Attendance />)} />
            <Route path="leave" element={G("leave", <Leave />)} />
            <Route path="suppliers" element={G("suppliers", <Suppliers />)} />
            <Route path="contracts" element={G("contracts", <Contracts />)} />
            <Route path="deliveries" element={G("deliveries", <Deliveries />)} />
            <Route path="purchases" element={G("purchases", <Purchases />)} />
            <Route path="feedback" element={G("feedback", <Feedback />)} />
            <Route path="view-feedback" element={G("feedback", <ViewFeedback />)} />
            <Route path="branch-feedback" element={G("feedback", <BranchFeedback />)} />
            <Route path="surveys" element={G("surveys", <Surveys />)} />
            <Route path="loyalty" element={G("loyalty", <LoyaltyMembers />)} />
            <Route path="business-cards" element={G("business-cards", <BusinessCards />)} />
            <Route path="messages" element={G("messages", <Messages />)} />
            <Route path="announcements" element={G("announcements", <Announcements />)} />
            <Route path="documents" element={G("documents", <Documents />)} />
            {/* Website admin */}
            <Route path="products" element={G("products", <Products />)} />
            <Route path="orders" element={G("orders", <Orders />)} />
            <Route path="news-admin" element={G("news-admin", <NewsAdmin />)} />
            <Route path="careers-admin" element={G("careers-admin", <CareersAdmin />)} />
            <Route path="hero-slides" element={G("hero-slides", <HeroSlides />)} />
            <Route path="branches" element={G("branches", <Branches />)} />
            <Route path="users" element={G("users", <Users />)} />
            <Route path="staff" element={G("staff", <Staff />)} />
            <Route path="approvals" element={G("approvals", <Approvals />)} />
            <Route path="admin-requests" element={G("admin-requests", <AdminRequests />)} />
            <Route path="approval-policies" element={G("approvals", <ApprovalPolicies />)} />
            <Route path="periodic-reports" element={G("periodic-reports", <PeriodicReports />)} />
            <Route path="audit-log" element={G("audit-log", <AuditLog />)} />
            <Route path="backups" element={G("backups", <Backups />)} />
            <Route path="policy" element={G("policy", <Policy />)} />
            <Route path="tenants" element={G("tenants", <TenantsAdmin />)} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;

